import { Icon } from '@coop/components';
import { ChevronRightIcon } from '@coop/icons';
import classnames from 'classnames';
import type { FC } from 'react';
import { JsonLd } from 'react-schemaorg';
import type { BreadcrumbList } from 'schema-dts';

import useReactRouting from '../../../../ecommerce/hooks/useReactRouting';
import { sendInteractionEvent } from '../../../tracking/utils';
import { ConditionalMultipleWrappers } from '../../atoms/ConditionalWrapper';
import styles from './Breadcrumbs.module.less';
import type {
    BreadcrumbItem,
    BreadcrumbsJsonLdSchemaProps,
    BreadcrumbsProps,
} from './Breadcrumbs.types';

const getUrl = (url: string) => {
    return url ? new URL(url, window.location.origin).toString() : undefined;
};

const BreadcrumbsJsonLdSchema: FC<React.PropsWithChildren<BreadcrumbsJsonLdSchemaProps>> = ({
    items,
}) => {
    return (
        <JsonLd<BreadcrumbList>
            item={{
                '@type': 'BreadcrumbList',
                itemListElement: items
                    .filter((item) => !!item.url)
                    .map((item, index) => ({
                        '@type': 'ListItem',
                        name: item.name,
                        item: getUrl(item.url),
                        position: index + 1,
                    })),
                '@context': 'https://schema.org',
            }}
        />
    );
};

const Breadcrumbs: FC<React.PropsWithChildren<BreadcrumbsProps>> = ({ items, wrapperClasses }) => {
    const { push } = useReactRouting();

    const handleLinkClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        item: BreadcrumbItem,
    ) => {
        e.stopPropagation();
        e.preventDefault();
        sendInteractionEvent({
            eventCategory: 'Navigation',
            eventAction: 'Breadcrumbs',
            eventLabel: e.currentTarget.innerText,
        });

        if (item.isLayoutRouterAsyncLink) {
            push(e.currentTarget.href, true);
        } else {
            push(e.currentTarget.pathname);
        }
    };

    const isBreadcumbActive = (index: number) => {
        return index === items.length - 1;
    };

    return (
        <>
            <BreadcrumbsJsonLdSchema items={items} />
            <nav aria-label="Du är här">
                <ol
                    className={classnames(
                        styles.Breadcrumbs,
                        styles['Breadcrumbs--small'],
                        wrapperClasses,
                    )}
                >
                    {items?.map((item, index) => {
                        return (
                            <li
                                key={`${item.name}_${item.type}`}
                                className={classnames(
                                    styles.Item,
                                    isBreadcumbActive(index) && styles.IsActive,
                                )}
                            >
                                {index >= 1 && (
                                    <Icon
                                        className={styles.Arrow}
                                        icon={ChevronRightIcon}
                                        style={{ marginRight: 4 }}
                                        aria-hidden
                                    />
                                )}
                                <ConditionalMultipleWrappers
                                    choices={[
                                        {
                                            condition: isBreadcumbActive(index),
                                            renderWrapper: (children) => (
                                                <div className="u-flex u-flexAlignCenter">
                                                    {children}
                                                </div>
                                            ),
                                        },
                                        {
                                            condition: !isBreadcumbActive(index),
                                            renderWrapper: (children) => (
                                                <a
                                                    href={item.url}
                                                    onClick={(e) => handleLinkClick(e, item)}
                                                    className="u-flex u-flexAlignCenter"
                                                >
                                                    {children}
                                                </a>
                                            ),
                                        },
                                    ]}
                                >
                                    <span
                                        aria-current={isBreadcumbActive(index) ? 'page' : undefined}
                                    >
                                        {item.name}
                                    </span>
                                </ConditionalMultipleWrappers>
                            </li>
                        );
                    })}
                </ol>
            </nav>
        </>
    );
};

export default Breadcrumbs;
